import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import type { Pagination } from '@/dtos';
import type { ReviewList } from '@/dtos/Review';
import axiosInstance from '@/plugins/axios';
import { createAppAsyncThunk } from '@/store/thunk';

interface ReviewsState {
  loading: boolean;
  list: ReviewList[];
  item: ReviewList | null;
  page: number;
  pageSize: number;
  total: number;
}

const initialState: ReviewsState = {
  loading: false,
  list: [],
  item: null,
  page: 0,
  pageSize: 8,
  total: 0
};

export const requestReviews = createAppAsyncThunk(
  'reviews/getList',
  async (data: { page: number; pageSize: number; title?: string; categoryIds?: string[] }) => {
    try {
      const response = await axiosInstance.get<undefined, Pagination<ReviewList>>('/public/reviews', {
        params: {
          page: data.page,
          pageSize: data.pageSize,
          ...(data.title ? { title: data.title } : {}),
          ...(data.categoryIds?.length ? { categoryId: data.categoryIds } : {})
        }
      });
      return response;
    } catch {
      return;
    }
  }
);

export const requestReview = createAppAsyncThunk('reviews/getItem', async (id: string) => {
  try {
    const response = await axiosInstance.get<undefined, ReviewList>(`/public/reviews/${id}`);
    return response;
  } catch {
    return;
  }
});

export const reviewsSlice = createSlice({
  name: 'reviews',
  initialState,
  reducers: {
    setPage: (state, action: PayloadAction<number>) => {
      state.page = action.payload;
    },
    setPageSize: (state, action: PayloadAction<number>) => {
      state.pageSize = action.payload;
    },
    resetReviews: (state) => {
      state.loading = false;
      state.list = [];
      state.item = null;
      state.page = 0;
      state.pageSize = 8;
      state.total = 0;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(requestReviews.pending, (state) => {
        state.loading = true;
      })
      .addCase(requestReviews.rejected, (state) => {
        // state.list = [];
        state.item = null;
        state.page = 0;
        state.pageSize = 8;
        state.total = 0;
        state.loading = false;
      })
      .addCase(requestReviews.fulfilled, (state, action) => {
        if (action.payload) {
          const isNewSearch = action.payload.page === 0;

          state.list = isNewSearch
            ? action.payload.items
            : [...state.list, ...action.payload.items.filter((i) => !state.list.find((l) => l.id === i.id))];

          state.total = action.payload.total;
          state.page = action.payload.page;
          state.pageSize = action.payload.pageSize;
        }
        state.loading = false;
      });
    builder
      .addCase(requestReview.pending, (state) => {
        state.item = null;
        state.loading = true;
      })
      .addCase(requestReview.rejected, (state) => {
        state.loading = false;
      })
      .addCase(requestReview.fulfilled, (state, action) => {
        if (action.payload) {
          state.item = action.payload;
        }
        state.loading = false;
      });
  }
});

export const { setPage, setPageSize } = reviewsSlice.actions;
